import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { hero, q1, q2, closing, cases } from "../data/content";
import { Expandable } from "../components/Expandable";
import { PriorityVisual, CaseVisual } from "../components/visuals";
import { PrintButton } from "../components/PrintButton";

const AnchorLink = ({ href, children, testid }) => (
  <a href={href} className="ic-btn" data-testid={testid}>
    {children}
  </a>
);

export default function Interview() {
  return (
    <div>
      {/* Hero */}
      <section
        className="relative overflow-hidden border-b border-[var(--border)]"
        style={{ backgroundImage: "var(--glow)" }}
      >
        <div className="mx-auto max-w-5xl px-5 py-14 sm:px-8 sm:py-20">
          <span className="ic-pill" data-testid="hero-eyebrow">{hero.eyebrow}</span>
          <h1 className="mt-5 max-w-3xl font-head text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl" data-testid="hero-headline">
            {hero.headline}
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-[var(--text-2)]" data-testid="hero-supporting">
            {hero.supporting}
          </p>
          <p className="mt-3 font-mono text-xs uppercase tracking-[0.14em] text-[var(--text-muted)]">{hero.attribution}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <AnchorLink href="#priorities" testid="hero-link-priorities">First priorities <ArrowDown size={15} /></AnchorLink>
            <AnchorLink href="#rsc" testid="hero-link-rsc">Partnership story <ArrowDown size={15} /></AnchorLink>
            <Link to="/work" className="ic-btn-primary ic-btn" data-testid="hero-link-work">More work <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        {/* Question 1 */}
        <section id="priorities" className="py-14 sm:py-16" data-testid="section-priorities">
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-[var(--accent)]">Question 1</p>
          <h2 className="mt-2 font-head text-2xl font-bold tracking-tight sm:text-3xl">{q1.heading}</h2>
          <p className="mt-4 max-w-3xl text-lg leading-relaxed text-[var(--text-2)]">{q1.lead}</p>

          <div className="mt-8">
            <PriorityVisual stages={q1.stages} />
          </div>

          <div className="mt-6 rounded-xl border-l-2 border-[var(--accent)] bg-[var(--surface)] px-5 py-4">
            <p className="text-[var(--text-2)]">{q1.reasoning}</p>
          </div>

          {/* What would need to be true */}
          <div className="mt-10">
            <h3 className="font-head text-xl font-semibold">What would need to be true</h3>
            <p className="mt-1 text-[var(--text-2)]">{q1.conditionsIntro}</p>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {q1.conditions.map((c, i) => (
                <div key={c.label} className="ic-surface p-4" data-testid={`q1-condition-${i + 1}`}>
                  <p className="font-head text-sm font-semibold text-[var(--accent)]">{c.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-[var(--text-2)]">{c.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Optional expansions */}
          <div className="mt-8 space-y-3">
            {q1.expansions.map((ex, i) => (
              <Expandable key={ex.title} title={ex.title} badge={ex.badge} testid={`q1-expand-${i + 1}`}>
                {ex.paragraphs?.map((p) => (
                  <p key={p} className="mb-2 leading-relaxed last:mb-0">{p}</p>
                ))}
                {ex.rows && (
                  <div className="space-y-2">
                    {ex.rows.map((r) => (
                      <div key={r.label} className="flex flex-col gap-1 border-b border-[var(--border)] pb-2 last:border-0 sm:flex-row sm:gap-4">
                        <span className="font-head text-sm font-semibold text-[var(--text)] sm:w-40 sm:shrink-0">{r.label}</span>
                        <span className="text-sm leading-relaxed">{r.body}</span>
                      </div>
                    ))}
                  </div>
                )}
                {ex.conclusion && <p className="mt-3 leading-relaxed">{ex.conclusion}</p>}
                {ex.source && (
                  <a href={ex.source.href} target="_blank" rel="noopener noreferrer" className="ic-link mt-3 text-sm" data-testid={`q1-expand-${i + 1}-source`}>
                    {ex.source.label} <ArrowRight size={14} />
                  </a>
                )}
              </Expandable>
            ))}
          </div>
        </section>

        {/* Question 2 */}
        <section id="rsc" className="border-t border-[var(--border)] py-14 sm:py-16" data-testid="section-rsc">
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-[var(--accent)]">Question 2</p>
          <h2 className="mt-2 font-head text-2xl font-bold tracking-tight sm:text-3xl">{q2.heading}</h2>
          <p className="mt-1 font-head text-lg text-[var(--text-2)]">{q2.subheading}</p>

          <h3 className="mt-6 max-w-3xl font-head text-xl font-semibold leading-snug">{q2.decisionHeadline}</h3>
          <p className="mt-4 max-w-3xl leading-relaxed text-[var(--text-2)]">{q2.summary}</p>

          <div className="mt-8">
            <CaseVisual visual="rsc" compact />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="ic-surface p-5">
              <p className="font-mono text-[0.62rem] uppercase tracking-wider text-[var(--text-muted)]">My role</p>
              <p className="mt-2 text-sm leading-relaxed text-[var(--text-2)]">{q2.role}</p>
            </div>
            <div className="ic-surface p-5">
              <p className="font-mono text-[0.62rem] uppercase tracking-wider text-[var(--text-muted)]">Outcome & pride</p>
              <p className="mt-2 text-sm leading-relaxed text-[var(--text-2)]">{q2.outcome}</p>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/work/rsc" className="ic-btn-primary ic-btn" data-testid="q2-link-full-case">Explore the full RSC case <ArrowRight size={15} /></Link>
            <Link to="/work" className="ic-btn" data-testid="q2-link-more-work">See five additional examples <ArrowRight size={15} /></Link>
          </div>
        </section>

        {/* Closing */}
        <section className="border-t border-[var(--border)] py-14 sm:py-16" data-testid="section-closing">
          <h2 className="font-head text-2xl font-bold tracking-tight sm:text-3xl">{closing.heading}</h2>
          <ul className="mt-6 space-y-3">
            {closing.questions.map((qt) => (
              <li key={qt} className="ic-surface flex items-start gap-3 p-4">
                <span className="mt-0.5 text-[var(--accent)]"><ArrowRight size={18} /></span>
                <span className="leading-relaxed text-[var(--text-2)]">{qt}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm italic text-[var(--text-muted)]">{closing.note}</p>
            <PrintButton />
          </div>
        </section>
      </div>
    </div>
  );
}
